import React, { useEffect } from "react"; // Import useEffect
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SidebarLayout } from "./components/catalyst/sidebar-layout";
import { SidebarContent } from "./layout/Sidebar";
import { Toaster } from "./components/ui/sonner";

// Pages
import ClusterListPage from "./pages/ClusterListPage";
import ItineraryDetailsPage from "./pages/ItineraryDetailsPage";
import GeneratePage from "./pages/GeneratePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProfilePage from "./pages/ProfilePage";
import RsmSchedulesPage from "./pages/RsmSchedulesPage";
import SavedSchedulesPage from "./pages/SavedSchedulesPage";
import RsmManagerPage from "./pages/RsmManagerPage";
import ErrorBoundary from "./components/ErrorBoundary";
import RegisterMicrosoftPage from "./pages/RegisterMicrosoftPage"; // Import the new page
import BuildItineraryPage from "./pages/BuildItineraryPage"; // Import the new page
import ManageEngagementsPage from "./pages/ManageEngagementsPage"; // Import manage engagements page
import ContactManagerPage from "./pages/ContactManagerPage"; // Import contact manager page
import SavedByoiPage from "./pages/SavedByoiPage"; // Import saved BYOI page

function RequireAuth({ children, role }) {
  const { authStatus, user, loading } = useAuth(); // Use authStatus
  const location = useLocation();

  if (loading) return <div className="p-10 text-center">Loading...</div>;

  // NEW: Handle pending registration state
  if (authStatus === "pending_registration") {
    // Allow access only to the registration completion page
    if (location.pathname !== "/register-microsoft") {
      return <Navigate to="/register-microsoft" state={{ from: location }} />;
    }
    // If already on the registration page, allow rendering
    return children;
  }

  // If authenticated, proceed with role check
  if (authStatus === "authenticated") {
    if (role && user?.role !== role) {
      if (user?.role === "RSM") {
        return <Navigate to="/rsm-schedules" replace />;
      } else {
        return <Navigate to="/" replace />;
      }
    }
    return children; // Role matches or no role required
  }

  // If unauthenticated (and not pending), redirect to login
  if (authStatus === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Fallback or handle other statuses if needed
  return <div className="p-10 text-center">Checking authentication...</div>;
}

// Component to redirect based on role or pending registration
function RoleBasedRedirect() {
  const { authStatus, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (authStatus === "pending_registration") {
        console.log("RoleBasedRedirect: Pending registration, redirecting...");
        navigate("/register-microsoft", { state: { from: location }, replace: true });
      } else if (authStatus === "authenticated") {
        console.log(`RoleBasedRedirect: Authenticated as ${user?.role}, redirecting...`);
        if (user?.role === "RSM") {
          navigate("/rsm-schedules", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } else if (authStatus === "unauthenticated") {
        console.log("RoleBasedRedirect: Unauthenticated, redirecting to login...");
        navigate("/login", { state: { from: location }, replace: true });
      }
    }
  }, [authStatus, user, loading, navigate, location]);

  // Show loading state while checks are in progress
  return <div className="p-10 text-center">Redirecting...</div>;
}

function AppRoutes() {
  const { authStatus, loading } = useAuth(); // Get auth status

  // Don't render routes until loading is finished to avoid premature redirects
  if (loading) {
    return <div className="p-10 text-center">Initializing...</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* New route for Microsoft registration completion */}
      <Route
        path="/register-microsoft"
        element={
            <RegisterMicrosoftPage />
            // No RequireAuth needed here, handled by top-level logic or page itself
        }
      />

      {/* Role-based redirect - use RoleBasedRedirect component */}
      <Route path="/home" element={<RoleBasedRedirect />} />

      {/* Protected Routes - Using updated RequireAuth */}
      <Route
        path="/"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <GeneratePage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/build-itinerary"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <BuildItineraryPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/schedules"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <ClusterListPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/saved-schedules"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <SavedSchedulesPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/saved-byoi"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <SavedByoiPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/rsm-manager"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <RsmManagerPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/manage-engagements"
        element={
          <RequireAuth role="BM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <ManageEngagementsPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/contact-manager"
        element={
          <RequireAuth> {/* Allow both BM and RSM */}
            <SidebarLayout sidebar={<SidebarContent />}>
              <ContactManagerPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/schedules/:clusterId"
        element={
          <RequireAuth> {/* Allow both roles */}
            <SidebarLayout sidebar={<SidebarContent />}>
              <ErrorBoundary>
                <ItineraryDetailsPage />
              </ErrorBoundary>
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/rsm-schedules"
        element={
          <RequireAuth role="RSM">
            <SidebarLayout sidebar={<SidebarContent />}>
              <RsmSchedulesPage />
            </SidebarLayout>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth> {/* Allow all authenticated users */}
            <SidebarLayout sidebar={<SidebarContent />}>
              <ProfilePage />
            </SidebarLayout>
          </RequireAuth>
        }
      />

      {/* Default redirect - point to /home to handle logic */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <AppRoutes /> { /* Use the new routing component */ }
        <Toaster />
      </AppProvider>
    </AuthProvider>
  );
}

export default App;

